/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  RouteDefinition,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";
import { useBankState } from "../hooks/bank-state.js";
import { usePreferences } from "../hooks/preferences.js";
import { useSessionState } from "../hooks/session.js";

export interface PaymentOptionProps {
  routeOperationDetails: RouteDefinition<{ wopid: string }>;
  routeCashout: RouteDefinition;
  routeChargeWallet: RouteDefinition;
  routeCashAcceptor: RouteDefinition;
  routeWireTransfer: RouteDefinition;
}

export function PaymentOptions({
  routeCashout,
  routeChargeWallet,
  routeCashAcceptor,
  routeWireTransfer,
  routeOperationDetails,
}: PaymentOptionProps): VNode {
  const { i18n } = useTranslationContext();
  const { config, url: backendUrl } = useBankCoreApiContext();
  const [bankState] = useBankState();
  const [preferences] = usePreferences();
  const { state: session } = useSessionState();
  const activeWithdrawal =
    session.status === "loggedIn" &&
    bankState.activeWithdrawal?.username === session.username &&
    bankState.activeWithdrawal.backendBaseUrl === backendUrl.href
      ? bankState.activeWithdrawal
      : undefined;
  const activeOperationId =
    activeWithdrawal?.operationId ?? bankState.currentWithdrawalOperationId;
  const walletHref = activeOperationId
    ? routeOperationDetails.url({ wopid: activeOperationId })
    : routeChargeWallet.url({});
  const cashAcceptorHref = activeOperationId
    ? routeOperationDetails.url({ wopid: activeOperationId })
    : routeCashAcceptor.url({});

  return (
    <section class="mt-8" aria-labelledby="account-actions-heading">
      <h2
        id="account-actions-heading"
        class="text-lg font-semibold text-onBackground"
      >
        <i18n.Translate>Actions</i18n.Translate>
      </h2>
      <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <ActionCard
          name="charge wallet"
          href={walletHref}
          icon={<WalletIcon />}
          title={i18n.str`Withdraw to Taler Wallet`}
          description={i18n.str`Move digital cash to your Taler Wallet.`}
        />
        {preferences.cashAcceptorSimulator ? (
          <ActionCard
            name="cash acceptor simulator"
            href={cashAcceptorHref}
            icon={<CashAcceptorIcon />}
            title={i18n.str`Simulate cash acceptor`}
            description={i18n.str`Simulate the wallet-first flow used by a physical cash acceptor. This account provides the funds.`}
          />
        ) : undefined}
        <ActionCard
          name="wire transfer"
          href={routeWireTransfer.url({})}
          icon={<TransferIcon />}
          title={i18n.str`Send bank transfer`}
          description={i18n.str`Transfer money to another bank account.`}
        />
        {config.allow_conversion ? (
          <ActionCard
            name="cash out"
            href={routeCashout.url({})}
            icon={<CashoutIcon />}
            title={i18n.str`Cash out`}
            description={i18n.str`Convert regional currency and send it to your cashout account.`}
          />
        ) : undefined}
      </div>
    </section>
  );
}

function ActionCard({
  href,
  name,
  icon,
  title,
  description,
}: {
  href: string;
  name: string;
  icon: ComponentChildren;
  title: string;
  description: string;
}): VNode {
  return (
    <a
      href={href}
      data-name={name}
      aria-label={title}
      class="group flex min-h-28 gap-4 rounded-lg border border-outlineVariant bg-white p-5 shadow-sm transition hover:border-primary/50 hover:shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primaryMuted text-primaryDark">
        {icon}
      </span>
      <span>
        <span class="block font-semibold text-onBackground group-hover:text-primaryDark">
          {title}
        </span>
        <span class="mt-1 block text-sm leading-5 text-secondary">
          {description}
        </span>
      </span>
    </a>
  );
}

function WalletIcon(): VNode {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      class="h-6 w-6"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M3 7.5h15A3 3 0 0 1 21 10.5v7A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5v-12A2.5 2.5 0 0 1 5.5 3H18v4.5m0 5h3m-5 0h.01"
      />
    </svg>
  );
}

function TransferIcon(): VNode {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      class="h-6 w-6"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 8h15m0 0-4-4m4 4-4 4M20 16H5m0 0 4 4m-4-4 4-4"
      />
    </svg>
  );
}

function CashAcceptorIcon(): VNode {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      class="h-6 w-6"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M5 3h14v18H5V3Zm3 4h8m-8 4h8m-6 5h4"
      />
    </svg>
  );
}

function CashoutIcon(): VNode {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      class="h-6 w-6"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
      />
    </svg>
  );
}
